import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews, stats, partners } from '@/mocks/reviews';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9qtjh7nahb7382ovflg';

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    phoneAlt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formData.phoneAlt && formData.phoneAlt.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', phoneAlt: '' });
      return;
    }

    if (formData.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      payload.append('phone', formData.phone);
      payload.append('subject', formData.subject);
      payload.append('message', formData.message);

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', phoneAlt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : '网络错误，请稍后再试');
      setFormStatus('error');
    }
  }, [formData]);

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 黄瓜网站 | 专业黄瓜种植与交易平台"
        description="了解黄瓜网站的品牌故事、核心团队、合作伙伴及联系方式。黄瓜网站致力于为全国黄瓜种植户、经销商和农业爱好者提供全方位信息服务。"
        keywords="关于黄瓜网站,联系我们,黄瓜网站团队,合作伙伴"
        canonicalUrl="https://www.huanggua.site/about"
      />

      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />
      </div>

      <main>
        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-4">
              关于黄瓜网站
            </h1>
            <p className="text-foreground-500 text-sm md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              黄瓜网站成立于2020年，是国内领先的黄瓜产业综合信息服务平台。我们汇聚行业专家智慧，整合全国市场数据，为黄瓜产业链上下游用户提供种植技术、品种选育、病虫害防治、市场行情和产品交易等全方位服务。
            </p>
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20agricultural%20technology%20center%20with%20cucumber%20greenhouse%20research%20facility%2C%20professional%20team%20working%20in%20bright%20office%20space%2C%20sustainable%20farming%20innovation%2C%20warm%20natural%20lighting%2C%20clean%20corporate%20aesthetic%2C%20green%20plants%20throughout%20interior&width=1200&height=500&seq=about-hero-2026&orientation=landscape"
              alt="黄瓜网站团队与办公环境"
              className="w-full rounded-lg object-cover h-48 md:h-72"
            />
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600 mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              听听种植户和经销商们怎么说
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {reviews.map((review) => (
                <div key={review.id} className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full ${review.avatarColor} flex items-center justify-center text-background-50 font-bold text-sm flex-shrink-0`}>
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-900 text-sm">{review.name}</h3>
                      <p className="text-xs text-foreground-400">{review.location} · {review.role}</p>
                    </div>
                    <div className="ml-auto flex items-center">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-background-300'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {review.content}
                  </p>
                  <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partners */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-2">
              合作伙伴
            </h2>
            <p className="text-sm text-foreground-500 mb-8">
              与行业顶尖机构携手，共同服务黄瓜产业
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {partners.map((partner, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-background-50 border border-background-200 rounded-lg text-left">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-primary-600 text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{partner.name}</p>
                    <p className="text-xs text-foreground-400">{partner.type}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或合作意向，欢迎与我们联系
            </p>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-800 text-sm">客服电话</h3>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                      <p className="text-xs text-foreground-400">工作日 9:00-18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-800 text-sm">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">contact@huanggua.site</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-800 text-sm">公司地址</h3>
                      <p className="text-sm text-foreground-500">山东省潍坊市寿光蔬菜高科技示范园</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form data-readdy-form="true" id="contact-form" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-600 mb-1">
                      电话
                    </label>
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的电话"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入咨询主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-[10px] text-foreground-400 mt-1 text-right">
                      {formData.message.length}/500
                    </p>
                  </div>

                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      value={formData.phoneAlt}
                      onChange={handleChange}
                    />
                  </div>

                  {formStatus === 'error' && formError && (
                    <div className="text-xs text-accent-600 bg-accent-50 border border-accent-200 rounded-lg p-3">
                      {formError}
                    </div>
                  )}

                  {formStatus === 'success' && (
                    <div className="text-xs text-primary-600 bg-primary-50 border border-primary-200 rounded-lg p-3">
                      提交成功！感谢您的留言，我们会尽快与您联系。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交留言'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}